import { formatNumber } from 'util/formatNumber';
import { STATES } from 'util/types/states';
import { Box, Typography, LinearProgress, Switch } from '@mui/material';
import { MinusBoxIcon, ImagePaperIcon, ImageIcon } from 'components/icons';
import { Image, Link } from 'components/lib';
import {
  Surface,
  TextInput,
  FormErrors,
  Select,
  AddLink,
  ButtonLink,
  Button,
} from 'components/ui';
import { ErrorMessages } from 'forms/error-messages';
import { MAX_IMAGE_FILE_SIZE_MB } from 'pages/admin/details';
import React, { ReactElement, useEffect, useState, useRef } from 'react';
import { Routes } from 'services/routes';
import { UpdateSchoolForm, updateSchoolFormSchema } from 'forms';
import {
  useProfile,
  useIsMasterAccount,
  useRequireRole,
  useAppForm,
} from 'hooks';
import { useImageUpload } from 'hooks/useImageUpload';
import { useRouter } from 'next/router';
import { ProviderContext, useSnackbar } from 'notistack';
import {
  FieldArrayWithId,
  useFieldArray,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';
import { GetSchoolResponse, useSchoolsApi } from 'services/api';
import { CampusResourceUrls } from 'services/api/models/school/school-details.model';
import { CAMPUS_RESOURCE_FIELDS } from 'components/pages/home/for-you/shared';
import useSWR from 'swr';
import { AddGroupModal } from 'components/modals/AddGroupModal';
import TrashIcon from '../../../../assets/icons/trash.svg';
import { ApprovalFlowOrderTab, ApprovalFlowOrderRef } from './approval-flow-order';

const MAX_LOGO_FILE_SIZE_MB = 5;
const MAX_BANNER_FILE_SIZE_MB = 10;

export interface DetailsTabProps {
  schoolId: string;
  refreshSchool: () => void;
}

const ImageUploadSection = ({
  title,
  imageUrl,
  onOpenSelector,
  uploadProgress,
  maxFileSize,
  recommendedSize,
  width = 70,
  height = 70,
}: {
  title: string;
  imageUrl: string | null;
  onOpenSelector: () => void;
  uploadProgress?: number;
  maxFileSize: number;
  recommendedSize: string;
  width?: number;
  height?: number;
}) => (
  <Surface mt={40} mr={18} noInnerPadding position="relative" overflow="hidden">
    <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
      {title}
    </Typography>
    <Box mt={6} mb={5} display="flex">
      <Box mt={6} flex={1}>
        <Link onClick={onOpenSelector}>
          <Box
            display="flex"
            alignItems="center"
            tabIndex={0}
            className="keyboardFocus"
            onKeyPress={e => e.key === ' ' && onOpenSelector()}
          >
            <Box mr={10} lineHeight={0}>
              <ImagePaperIcon />
            </Box>
            <Typography sx={{ fontWeight: 600 }}>
              Click Here To Upload Image
            </Typography>
          </Box>
        </Link>
        <Box mt={6}>
          <Typography sx={{ fontSize: '0.875em' }}>
            JPEG or PNG. File size no larger than {maxFileSize} MB.
            <br />
            Recommended {recommendedSize}.
          </Typography>
        </Box>
      </Box>
      <Box
        mt={-20}
        flex={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {imageUrl ? (
          <Box
            lineHeight={0}
            style={{
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
            }}
            onClick={onOpenSelector}
          >
            <Image
              src={imageUrl}
              width={width}
              height={height}
              alt="Uploaded Image"
            />
          </Box>
        ) : (
          <Box
            py={10}
            px={10}
            lineHeight={0}
            style={{
              background: '#efefef',
              color: '#d5d5d5',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
            }}
            onClick={onOpenSelector}
          >
            <ImageIcon style={{ fontSize: 52 }} />
          </Box>
        )}
      </Box>
    </Box>
    {uploadProgress && (
      <LinearProgress
        variant="determinate"
        value={uploadProgress}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 8,
        }}
      />
    )}
  </Surface>
);

const BannerImageSection = ({
  bannerUrl,
  onOpenSelector,
  uploadProgress,
}: {
  bannerUrl: string | null;
  onOpenSelector: () => void;
  uploadProgress?: number;
}) => (
  <Surface mt={40} mr={18} noInnerPadding position="relative" overflow="hidden">
    <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
      School Banner Image
    </Typography>

    <Box mt={12}>
      <Link onClick={onOpenSelector}>
        <Box
          display="flex"
          alignItems="center"
          tabIndex={0}
          className="keyboardFocus"
          onKeyPress={e => e.key === ' ' && onOpenSelector()}
        >
          <Box mr={10} lineHeight={0}>
            <ImagePaperIcon />
          </Box>
          <Typography sx={{ fontWeight: 600 }}>
            Click Here To Upload Image
          </Typography>
        </Box>
      </Link>
    </Box>

    <Box
      mt={12}
      tabIndex={0}
      onClick={onOpenSelector}
      onKeyPress={e => {
        if (e.key === ' ' || e.key === 'Enter') {
          onOpenSelector();
          e.preventDefault();
        }
      }}
      className="keyboardFocus"
      lineHeight={0}
    >
      {bannerUrl ? (
        <Image
          src={bannerUrl}
          alt="School Banner image"
          style={{
            width: 375,
            minHeight: 150,
            maxHeight: 400,
          }}
        />
      ) : (
        <Box
          height={250}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          lineHeight={0}
          style={{
            background: '#efefef',
            color: '#d5d5d5',
            cursor: 'pointer',
          }}
        >
          <ImageIcon style={{ fontSize: 80 }} />
          <Typography
            color="primary"
            style={{
              fontSize: '0.875em',
              fontWeight: 600,
            }}
          >
            Click Here To Upload Image
          </Typography>
          <Typography
            color="textPrimary"
            align="center"
            style={{
              fontSize: '0.875em',
            }}
          >
            JPEG or PNG. File size no larger than {MAX_BANNER_FILE_SIZE_MB} MB.
            Recommended {formatNumber(1250)} x {formatNumber(500)} pixels.
          </Typography>
        </Box>
      )}
    </Box>

    {uploadProgress && (
      <LinearProgress
        variant="determinate"
        value={uploadProgress}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 8,
        }}
      />
    )}
  </Surface>
);

const FormField = ({
  register,
  name,
  label,
  defaultValue,
  error,
  autoComplete = 'off',
  minWidth = 262,
}: {
  register: UseFormRegister<UpdateSchoolForm>;
  name: string;
  label: string;
  defaultValue?: string;
  error?: string;
  autoComplete?: string;
  minWidth?: number;
}) => (
  <Box minWidth={minWidth}>
    <TextInput
      {...register(name as keyof UpdateSchoolForm)}
      autoComplete={autoComplete}
      label={label}
      defaultValue={defaultValue}
    />
    {error && <FormErrors>{error}</FormErrors>}
  </Box>
);

const AliasField = ({
  field,
  index,
  register,
  onRemove,
  error,
}: {
  field: FieldArrayWithId<UpdateSchoolForm, 'aliases', 'id'>;
  index: number;
  register: UseFormRegister<UpdateSchoolForm>;
  onRemove: () => void;
  error?: string;
}) => (
  <React.Fragment key={field.id}>
    <Box
      mt={index === 0 ? 20 : 0}
      width={300}
      display="flex"
      alignItems="flex-end"
    >
      <Box
        mb={16}
        pr={10}
        lineHeight={0}
        style={{ cursor: 'pointer' }}
        onClick={onRemove}
      >
        <MinusBoxIcon color="error" style={{ fontSize: '1.125em' }} />
      </Box>
      <TextInput
        label="School Alias"
        autoComplete="off"
        {...register(`aliases.${index}.value`)}
      />
    </Box>
    <Box pb={20}>
      {error && (
        <Box pl={30}>
          <FormErrors>{error}</FormErrors>
        </Box>
      )}
    </Box>
  </React.Fragment>
);

const useImageValidation = (
  enqueueSnackbar: ProviderContext['enqueueSnackbar'],
) => {
  const validateImages = (
    iconUrl: string | null,
    logoUrl: string | null,
    bannerUrl: string | null,
  ) => {
    if (!iconUrl) {
      enqueueSnackbar('Please upload an icon', { variant: 'error' });
      return false;
    }

    if (!logoUrl) {
      enqueueSnackbar('Please upload a logo', { variant: 'error' });
      return false;
    }

    if (!bannerUrl) {
      enqueueSnackbar('Please upload a banner', { variant: 'error' });
      return false;
    }

    return true;
  };

  return { validateImages };
};

export const DetailsTab = ({
  schoolId,
  refreshSchool,
}: DetailsTabProps): ReactElement | null => {
  const { loading } = useProfile();
  const isMaster = useIsMasterAccount();
  useRequireRole('master');

  const schoolsApi = useSchoolsApi();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { validateImages } = useImageValidation(enqueueSnackbar);
  const rSchool = useSWR<GetSchoolResponse>(
    schoolId ? [schoolsApi.getSchool, schoolId] : null,
  );

  const {
    formState: { errors },
    ...form
  } = useAppForm(updateSchoolFormSchema, {
    defaultValues: {
      aliases: [],
    },
    mode: 'all',
  });

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [
    isMultiStepApprovalEnabled,
    setIsMultiStepApprovalEnabled,
  ] = useState<boolean>(false);
  const [
    isSequentialApprovalEnabled,
    setIsSequentialApprovalEnabled,
  ] = useState<boolean>(false);
  const [groups, setGroups] = useState<string[]>([]);
  const [addGroupModalOpen, setAddGroupModalOpen] = useState<boolean>(false);
  const [campusResourceUrls, setCampusResourceUrls] = useState<
    Record<keyof CampusResourceUrls, string>
  >({
    counseling: '',
    scholarships: '',
    career: '',
    studentSuccess: '',
    health: '',
  });
  const [
    campusResourceErrors,
    setCampusResourceErrors,
  ] = useState<Partial<Record<keyof CampusResourceUrls, string>>>({});
  const approvalFlowOrderRef = useRef<ApprovalFlowOrderRef>(null);

  const logoUpload = useImageUpload({ maxMb: MAX_LOGO_FILE_SIZE_MB });
  const bannerUpload = useImageUpload({ maxMb: MAX_BANNER_FILE_SIZE_MB });
  const iconUpload = useImageUpload({ maxMb: MAX_LOGO_FILE_SIZE_MB });

  useEffect(() => {
    setLogoUrl(logoUpload.url);
  }, [logoUpload.url]);

  useEffect(() => {
    setBannerUrl(bannerUpload.url);
  }, [bannerUpload.url]);

  useEffect(() => {
    setIconUrl(iconUpload.url);
  }, [iconUpload.url]);

  useEffect(() => {
    if (!rSchool.data) return;

    form.reset({
      state: rSchool.data.address.state,
      aliases: rSchool.data.aliases?.map(alias => ({ value: alias })) ?? [],
    });

    setIconUrl(rSchool.data.iconUrl);
    setLogoUrl(rSchool.data.logoUrl);
    setBannerUrl(rSchool.data.bannerUrl);
    setIsMultiStepApprovalEnabled(rSchool.data.isMultiStepApprovalEnabled);
    setIsSequentialApprovalEnabled(rSchool.data.isSequentialApprovalEnabled);
    setGroups(rSchool.data.approvalTitles ?? []);

    const savedUrls = rSchool.data.campusResourceUrls ?? {};
    setCampusResourceUrls({
      counseling: savedUrls.counseling ?? '',
      scholarships: savedUrls.scholarships ?? '',
      career: savedUrls.career ?? '',
      studentSuccess: savedUrls.studentSuccess ?? '',
      health: savedUrls.health ?? '',
    });
    setCampusResourceErrors({});
  }, [rSchool.data]);

  const handleAddGroup = (title: string) => {
    if (groups.includes(title)) {
      enqueueSnackbar('Group already exists', { variant: 'error' });
      return;
    }
    setGroups([...groups, title]);
    setAddGroupModalOpen(false);
  };

  const isValidHttpUrl = (value: string): boolean => {
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const validateCampusResourceUrls = (): CampusResourceUrls | null => {
    const errors: Partial<Record<keyof CampusResourceUrls, string>> = {};
    const sanitized: CampusResourceUrls = {};

    for (const field of CAMPUS_RESOURCE_FIELDS) {
      const value = (campusResourceUrls[field.key] ?? '').trim();
      if (!value) continue;
      if (!isValidHttpUrl(value)) {
        errors[field.key] = 'Enter a valid URL (https:// or http://)';
        continue;
      }
      sanitized[field.key] = value;
    }

    setCampusResourceErrors(errors);
    if (Object.keys(errors).length) return null;
    return sanitized;
  };

  const save = async (data: UpdateSchoolForm) => {
    if (!validateImages(iconUrl, logoUrl, bannerUrl)) return;

    const sanitizedCampusResourceUrls = validateCampusResourceUrls();
    if (sanitizedCampusResourceUrls === null) {
      enqueueSnackbar('Please fix the Campus Resources URLs', {
        variant: 'error',
      });
      return;
    }

    // Validate approval flow order if multi-step approval is enabled
    if (isMultiStepApprovalEnabled && approvalFlowOrderRef.current) {
      const validationError = approvalFlowOrderRef.current.validate();
      if (validationError) {
        enqueueSnackbar(validationError, {
          variant: 'error',
        });
        return;
      }
    }

    const result = await schoolsApi.saveSchool(schoolId, {
      ...data,
      iconUrl,
      logoUrl,
      bannerUrl,
      isMultiStepApprovalEnabled,
      isSequentialApprovalEnabled,
      aliases: data.aliases?.map(({ value }) => value) ?? [],
      approvalTitles: groups ?? [],
      termsUrl: data.termsUrl,
      termsDisplayName: data.termsDisplayName,
      campusResourceUrls: sanitizedCampusResourceUrls,
    });

    if (!result.success) {
      enqueueSnackbar(result.error.response?.data.message, {
        variant: 'error',
      });
      refreshSchool();
      return;
    }

    // Save approval flow order if multi-step approval is enabled
    if (isMultiStepApprovalEnabled && approvalFlowOrderRef.current) {
      try {
        const orderData = approvalFlowOrderRef.current.getOrderValues();
        if (orderData) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          const orderResult = await schoolsApi.updateApprovalFlowOrder(schoolId, orderData);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (!orderResult.success) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const errorMessage = orderResult.error.response?.data?.message || 'Failed to update approval flow order';
            enqueueSnackbar(
              typeof errorMessage === 'string' ? errorMessage : 'Failed to update approval flow order',
              { variant: 'error' },
            );
            refreshSchool();
            return;
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to update approval flow order';
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
        return;
      }
    }

    const id = result.value.id;

    enqueueSnackbar('Successfully updated school details', {
      variant: 'success',
    });
    refreshSchool();
    await router.push(Routes.master.school(id));
  };

  const aliases = useFieldArray({
    control: form.control,
    name: 'aliases',
  });

  useWatch({
    control: form.control,
    name: 'aliases',
    defaultValue: [],
  });

  const state = useWatch({
    control: form.control,
    name: 'state',
    defaultValue: rSchool.data?.address.state,
  });

  const stateSlug = (state || 'state').toLowerCase().replace(/\s+/g, '-');

  if (loading || !isMaster) return null;

  let defaultSlug = rSchool.data?.slug.toLowerCase() ?? '';
  if (defaultSlug.startsWith(`${stateSlug}/`))
    defaultSlug = defaultSlug.substring(stateSlug.length + 1);

  return (
    <form onSubmit={form.handleSubmit(save)}>
      <Box maxWidth={892}>
        <Surface mt={20} mr={18} noInnerPadding>
          <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
            School Information
          </Typography>
          <Box
            my={20}
            display="flex"
            flexWrap="wrap"
            style={{ rowGap: 40, columnGap: 50 }}
          >
            <FormField
              register={form.register}
              name="name"
              label="School Name"
              defaultValue={rSchool.data?.name}
              error={errors.name?.message}
            />
            <FormField
              register={form.register}
              name="websiteUrl"
              label="School Website"
              defaultValue={rSchool.data?.websiteUrl}
              error={errors.websiteUrl?.message}
            />
            <FormField
              register={form.register}
              name="streetAddress"
              label="Street Address"
              defaultValue={rSchool.data?.address.streetAddress}
              error={errors.streetAddress?.message}
            />
            <FormField
              register={form.register}
              name="city"
              label="City"
              defaultValue={rSchool.data?.address.city}
              error={errors.city?.message}
            />
            <Box minWidth={262}>
              <Select
                {...form.register('state')}
                label="State"
                defaultValue={rSchool.data?.address.state}
              >
                <>
                  <option></option>
                  {STATES.map(state => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </>
              </Select>
              {errors.state?.message && (
                <FormErrors>{ErrorMessages.Required}</FormErrors>
              )}
            </Box>
            <FormField
              register={form.register}
              name="zipCode"
              label="Zip Code"
              defaultValue={rSchool.data?.address.zipCode}
              error={errors.zipCode?.message}
            />
            <FormField
              register={form.register}
              name="termsUrl"
              label="T&C URL"
              defaultValue={rSchool.data?.termsUrl}
              error={errors.termsUrl?.message}
            />
            <FormField
              register={form.register}
              name="termsDisplayName"
              label="T&C Display Name"
              defaultValue={rSchool.data?.termsDisplayName}
              error={errors.termsDisplayName?.message}
            />
          </Box>
        </Surface>

        <Surface mt={40} mr={18} noInnerPadding>
          <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
            School Email Domain
          </Typography>
          <Box maxWidth={300}>
            <Typography>{rSchool.data?.domainName}</Typography>
          </Box>
        </Surface>

        <Surface mt={40} mr={18} noInnerPadding>
          <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
            Alternative School Names
          </Typography>
          {aliases.fields.map((field, i: number) => (
            <AliasField
              key={field.id}
              field={field}
              index={i}
              register={form.register}
              onRemove={() => aliases.remove(i)}
              error={errors?.aliases?.[i]?.value?.message}
            />
          ))}
          <Box mt={10}>
            <AddLink onClick={() => aliases.append({ value: '' })}>
              Add Alias
            </AddLink>
          </Box>
        </Surface>

        <Surface mt={40} mr={18} noInnerPadding>
          <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
            Campus Resources
          </Typography>
          <Typography sx={{ fontSize: '0.875em', mt: '4px' }}>
            Set the destination URL for each Campus Resources row shown on the
            student dashboard. Leave a field blank to hide that row.
          </Typography>
          <Box
            my={20}
            display="flex"
            flexDirection="column"
            style={{ rowGap: 20 }}
          >
            {CAMPUS_RESOURCE_FIELDS.map(field => (
              <Box key={field.key} minWidth={262} maxWidth={400}>
                <TextInput
                  label={field.label}
                  autoComplete="off"
                  hasError={Boolean(campusResourceErrors[field.key])}
                  value={campusResourceUrls[field.key]}
                  onChange={e =>
                    setCampusResourceUrls(prev => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                />
                {campusResourceErrors[field.key] && (
                  <FormErrors>{campusResourceErrors[field.key]}</FormErrors>
                )}
              </Box>
            ))}
          </Box>
        </Surface>

        <Surface mt={40} mr={18} noInnerPadding>
          <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
            Multi-Step Approval
          </Typography>
          <Box mt={10}>
            <Switch
              onChange={e => setIsMultiStepApprovalEnabled(e.target.checked)}
              checked={isMultiStepApprovalEnabled}
            />
          </Box>
        </Surface>

        {isMultiStepApprovalEnabled && (
          <ApprovalFlowOrderTab
            ref={approvalFlowOrderRef}
            schoolId={schoolId}
            isMultiStepApprovalEnabled={isMultiStepApprovalEnabled}
            isSequentialApprovalEnabled={isSequentialApprovalEnabled}
            schoolData={rSchool.data}
          />
        )}

        {isMultiStepApprovalEnabled && (
          <>
            <Surface mt={40} mr={18} noInnerPadding>
              <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
                Sequential Approval
              </Typography>
              <Box mt={10}>
                <Switch
                  onChange={e =>
                    setIsSequentialApprovalEnabled(e.target.checked)
                  }
                  checked={isSequentialApprovalEnabled}
                />
              </Box>
            </Surface>

            {isSequentialApprovalEnabled && (
              <Surface mt={40} mr={18} noInnerPadding>
                <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
                  Groups
                </Typography>
                <Box mt={10}>
                  {groups.map(group => (
                    <Box
                      key={group}
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent={'space-between'}
                      height={'48px'}
                    >
                      <Typography
                        sx={{
                          color: '##383838',
                          fontSize: 14,
                          fontWeight: 400,
                        }}
                      >
                        {group}
                      </Typography>
                      <TrashIcon
                        color="#9B0000"
                        style={{
                          fontSize: '1.125em',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          setGroups(groups.filter(g => g !== group));
                        }}
                      />
                    </Box>
                  ))}
                </Box>
                <Box mt={10} height={37} display={'flex'} alignItems={'center'}>
                  <AddLink onClick={() => setAddGroupModalOpen(true)}>
                    Add approval titles
                  </AddLink>
                </Box>
              </Surface>
            )}
          </>
        )}

        <Surface mt={40} mr={18} noInnerPadding>
          <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
            Custom CampusLush URL Slug
          </Typography>
          <Box my={10} display="flex" alignItems="center" style={{ gap: 2 }}>
            <Typography>
              {window.location.host}/{stateSlug}/
            </Typography>
            <TextInput {...form.register('slug')} defaultValue={defaultSlug} />
          </Box>
        </Surface>

        <ImageUploadSection
          title="School Profile Icon"
          imageUrl={iconUrl}
          onOpenSelector={iconUpload.open}
          uploadProgress={iconUpload.progress ?? 0}
          maxFileSize={MAX_IMAGE_FILE_SIZE_MB}
          recommendedSize="200 x 200 pixels"
        />

        <ImageUploadSection
          title="School Logo"
          imageUrl={logoUrl}
          onOpenSelector={logoUpload.open}
          uploadProgress={logoUpload.progress ?? 0}
          maxFileSize={MAX_IMAGE_FILE_SIZE_MB}
          recommendedSize="340 x 120 pixels"
        />

        <BannerImageSection
          bannerUrl={bannerUrl}
          onOpenSelector={bannerUpload.open}
          uploadProgress={bannerUpload.progress ?? 0}
        />

        <Box mt={40} display="flex">
          <Box mr={30}>
            <ButtonLink inverted minHeight={50} href={Routes.master.schools()}>
              Back
            </ButtonLink>
          </Box>
          <Button variant="vibrant" minHeight={50} type="submit">
            Save School
          </Button>
        </Box>
      </Box>

      {addGroupModalOpen && (
        <AddGroupModal
          open={addGroupModalOpen}
          onClose={() => setAddGroupModalOpen(false)}
          onSuccess={handleAddGroup}
        />
      )}
    </form>
  );
};
